import { getShape, getLineLength } from '@/modules/Node';
import type { Point, EdgeData } from '@/modules/Node';
import { EDGE_LINES, getEdgeType } from '@/modules/Edge';
import type { EdgeLineBounds, EdgeArrowsBounds, EdgeShadowBounds, EdgeTitleBounds, EdgeTypeValue, ExitSide } from '@/modules/Edge';
import { TEXT_POSITION } from '@/models/TEXT_POSITION';
import { measureTextDimensions } from '@/services/canvas/text/CanvasTextService';

interface EdgeContent {
  start: string | null;
  middle: string | null;
  end: string | null;
}

interface ParsedContent {
  content: EdgeContent;
  amount: number;
}

interface EdgeForBounds {
  name?: string;
  editing: boolean | null;
  borderWidth: number;
  _HOVER_AREA: number;
  _BASE_CANVAS_SIZE: number;
  _ARROW_HEIGHT: number;
  _ARROW_DOT_RADIUS: number;
  _SHADOW_BLUR_MULTIPLIER: number;
  fromNodeShape: string;
  toNodeShape: string;
  startPosX: number;
  startPosY: number;
  endPosX: number;
  endPosY: number;
  fromNodeWidth: number;
  fromNodeHeight: number;
  fromNodeBorderWidth: number;
  fromNodeBorderWidthValue: number;
  toNodeWidth: number;
  toNodeHeight: number;
  toNodeBorderWidth: number;
  toNodeBorderWidthValue: number;
  lineStart: string;
  lineEnd: string;
  lineStartSize: string | number;
  lineEndSize: string | number;
  shadow: boolean;
  shadowBlur: number;
  shadowX: number;
  shadowY: number;
  content: EdgeContent | null;
  contentMiddle: string | null;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  textPosition: string;
  ctx: CanvasRenderingContext2D;
  type?: string;
  routingType?: EdgeTypeValue;

  // Mutable properties set as side effects
  fromPoint: Point;
  toPoint: Point;
  edgeBounds: EdgeLineBounds;
  arrowsBounds: EdgeArrowsBounds;
  shadowBounds: EdgeShadowBounds;
  titleBounds: EdgeTitleBounds;
}

interface BoundsResult {
  width: number | null;
  height: number | null;
}

function _getFromPoint(edge: EdgeForBounds): Point {
  const shapeDef = getShape(edge.fromNodeShape);
  const { x, y } = shapeDef.getEdgeConnectionPoint(edge, 'from');
  return edge.fromPoint = { x, y };
}

function _getToPoint(edge: EdgeForBounds): Point {
  const shapeDef = getShape(edge.toNodeShape);
  const { x, y } = shapeDef.getEdgeConnectionPoint(edge, 'to');
  return edge.toPoint = { x, y };
}

function _getEdgeTemplateBounds(edge: EdgeForBounds): EdgeLineBounds {
  return edge.edgeBounds = { width: edge._BASE_CANVAS_SIZE, height: edge.borderWidth, x: 0, y: 0 };
}

function _getSideTarget(centerX: number, centerY: number, side: ExitSide): Point {
  const FAR = 10000;
  switch (side) {
    case 'right':  return { x: centerX + FAR, y: centerY };
    case 'left':   return { x: centerX - FAR, y: centerY };
    case 'bottom': return { x: centerX, y: centerY + FAR };
    case 'top':    return { x: centerX, y: centerY - FAR };
  }
}

function _getEdgeBounds(edge: EdgeForBounds): EdgeLineBounds {
  const edgeComputedHeight = edge.editing ? edge.borderWidth + edge._HOVER_AREA : edge.borderWidth;
  const edgeBounds: EdgeLineBounds = { width: 0, height: edgeComputedHeight, x: 0, y: 0 };

  const edgeType = getEdgeType(edge.routingType ?? 'straight');

  let fromPoint: Point;
  let toPoint: Point;

  if (edgeType.getExitSides) {
    const { exit, entry } = edgeType.getExitSides(
      edge.startPosX, edge.startPosY, edge.endPosX, edge.endPosY
    );

    // Create fake edge data targeting the exit direction so shape geometry is used
    const exitTarget = _getSideTarget(edge.startPosX, edge.startPosY, exit);
    const fromEdge: EdgeData = {
      startPosX: edge.startPosX, startPosY: edge.startPosY,
      endPosX: exitTarget.x, endPosY: exitTarget.y,
      fromNodeWidth: edge.fromNodeWidth, fromNodeHeight: edge.fromNodeHeight,
      fromNodeBorderWidth: edge.fromNodeBorderWidth, fromNodeBorderWidthValue: edge.fromNodeBorderWidthValue,
      toNodeWidth: edge.toNodeWidth, toNodeHeight: edge.toNodeHeight,
      toNodeBorderWidth: edge.toNodeBorderWidth, toNodeBorderWidthValue: edge.toNodeBorderWidthValue,
      fromNodeShape: edge.fromNodeShape, toNodeShape: edge.toNodeShape,
    };
    const fromShape = getShape(edge.fromNodeShape);
    fromPoint = edge.fromPoint = fromShape.getEdgeConnectionPoint(fromEdge, 'from');

    const entryTarget = _getSideTarget(edge.endPosX, edge.endPosY, entry);
    const toEdge: EdgeData = {
      startPosX: entryTarget.x, startPosY: entryTarget.y,
      endPosX: edge.endPosX, endPosY: edge.endPosY,
      fromNodeWidth: edge.fromNodeWidth, fromNodeHeight: edge.fromNodeHeight,
      fromNodeBorderWidth: edge.fromNodeBorderWidth, fromNodeBorderWidthValue: edge.fromNodeBorderWidthValue,
      toNodeWidth: edge.toNodeWidth, toNodeHeight: edge.toNodeHeight,
      toNodeBorderWidth: edge.toNodeBorderWidth, toNodeBorderWidthValue: edge.toNodeBorderWidthValue,
      fromNodeShape: edge.fromNodeShape, toNodeShape: edge.toNodeShape,
    };
    const toShape = getShape(edge.toNodeShape);
    toPoint = edge.toPoint = toShape.getEdgeConnectionPoint(toEdge, 'to');
  } else {
    fromPoint = _getFromPoint(edge);
    toPoint = _getToPoint(edge);
  }

  edgeBounds.width = getLineLength(fromPoint.x, toPoint.x, fromPoint.y, toPoint.y);

  return edge.edgeBounds = edgeBounds;
}

function _getArrowsBounds(edge: EdgeForBounds): EdgeArrowsBounds {
  const arrowsBounds: EdgeArrowsBounds = {
    startArrowWidth: 0, startArrowHeight: 0, startLabelPadding: 0,
    endArrowWidth: 0, endArrowHeight: 0, endLabelPadding: 0
  };
  const _arrowHeight = edge._ARROW_HEIGHT * edge.borderWidth;
  const _dotRadius = edge._ARROW_DOT_RADIUS * edge.borderWidth;
  const fromSizeMultiplier = parseInt(String(edge.lineStartSize)) ? Number(edge.lineStartSize) / 100 : 1;
  const toSizeMultiplier = parseInt(String(edge.lineEndSize)) ? Number(edge.lineEndSize) / 100 : 1;

  if (edge.lineStart !== EDGE_LINES.NONE.VALUE) {
    if (edge.lineStart === EDGE_LINES.DOT.VALUE) {
      const _arrowWidthHeight = _dotRadius * fromSizeMultiplier;
      arrowsBounds.startArrowWidth = arrowsBounds.startArrowHeight = _arrowWidthHeight;
      arrowsBounds.startLabelPadding = _arrowWidthHeight;
    } else {
      const _arrowRadius = _arrowHeight * fromSizeMultiplier / 2;

      if (edge.lineStart === EDGE_LINES.STRAIGHT.VALUE) {
        arrowsBounds.startArrowWidth = _arrowHeight * fromSizeMultiplier - _arrowHeight * fromSizeMultiplier / 4;
        arrowsBounds.startArrowHeight = _arrowRadius * 2 / 1.5;
        arrowsBounds.startLabelPadding = _arrowRadius + _arrowRadius / 2;
      }
      if (edge.lineStart === EDGE_LINES.SHARP.VALUE) {
        arrowsBounds.startArrowHeight = _arrowRadius * 2 / 1.5;
        arrowsBounds.startArrowWidth = _arrowHeight * fromSizeMultiplier - _arrowHeight * fromSizeMultiplier / 2;
        arrowsBounds.startLabelPadding = _arrowRadius + _arrowRadius / 3;
      }
      if (edge.lineStart === EDGE_LINES.WIDE.VALUE) {
        arrowsBounds.startArrowHeight = _arrowRadius * 2 / 1.7 + edge.borderWidth;
        arrowsBounds.startArrowWidth = edge.borderWidth * 5;
        arrowsBounds.startLabelPadding = _arrowRadius - _arrowRadius / 3 + (1 + edge.borderWidth);
      }
    }
  }

  if (edge.lineEnd !== EDGE_LINES.NONE.VALUE) {
    if (edge.lineEnd === EDGE_LINES.DOT.VALUE) {
      const _arrowWidthHeight = _dotRadius * toSizeMultiplier;
      arrowsBounds.endArrowWidth = arrowsBounds.endArrowHeight = _arrowWidthHeight;
      arrowsBounds.endLabelPadding = _arrowWidthHeight;
    } else {
      const _arrowRadius = _arrowHeight * toSizeMultiplier / 2;

      if (edge.lineEnd === EDGE_LINES.STRAIGHT.VALUE) {
        arrowsBounds.endArrowWidth = _arrowHeight * toSizeMultiplier - _arrowHeight * toSizeMultiplier / 4;
        arrowsBounds.endArrowHeight = _arrowRadius * 2 / 1.5;
        arrowsBounds.endLabelPadding = _arrowRadius + _arrowRadius / 2;
      }
      if (edge.lineEnd === EDGE_LINES.SHARP.VALUE) {
        arrowsBounds.endArrowHeight = _arrowRadius * 2 / 1.5;
        arrowsBounds.endArrowWidth = _arrowHeight * toSizeMultiplier - _arrowHeight * toSizeMultiplier / 2;
        arrowsBounds.endLabelPadding = _arrowRadius + _arrowRadius / 3;
      }
      if (edge.lineEnd === EDGE_LINES.WIDE.VALUE) {
        arrowsBounds.endArrowHeight = _arrowRadius * 2 / 1.7 + edge.borderWidth;
        arrowsBounds.endArrowWidth = edge.borderWidth * 5;
        arrowsBounds.endLabelPadding = _arrowRadius - _arrowRadius / 3 + (1 + edge.borderWidth);
      }
    }
  }

  return edge.arrowsBounds = arrowsBounds;
}

function _getShadowBounds(edge: EdgeForBounds): EdgeShadowBounds {
  let shadowBounds: EdgeShadowBounds = {
    width: null, height: null, x: null, y: null, blurWidth: null, shadowShiftX: null, shadowShiftY: null
  };
  const { startArrowHeight, endArrowHeight } = edge.arrowsBounds;
  const arrowMaxHeight = Math.max(startArrowHeight, endArrowHeight);

  if (!edge.shadow) {
    shadowBounds = Object.assign({}, edge.edgeBounds) as unknown as EdgeShadowBounds;
    shadowBounds.x = 0;
    shadowBounds.y = 0;
    shadowBounds.blurWidth = 0;
    shadowBounds.shadowShiftX = 0;
    shadowBounds.shadowShiftY = 0;
  } else {
    const blurWidth = edge.shadowBlur * edge._SHADOW_BLUR_MULTIPLIER;
    shadowBounds.blurWidth = blurWidth;
    shadowBounds.shadowShiftX = edge.shadowX;
    shadowBounds.shadowShiftY = edge.shadowY;
    shadowBounds.width = edge.edgeBounds.width + blurWidth * 2;
    shadowBounds.height = edge.edgeBounds.height + arrowMaxHeight + blurWidth * 2;

    shadowBounds.x = edge.shadowX - blurWidth;
    shadowBounds.y = edge.shadowY + arrowMaxHeight / 2 - blurWidth;
  }

  return edge.shadowBounds = shadowBounds;
}

function _contentParsed(content: EdgeContent): ParsedContent {
  const parsedContent: EdgeContent = {
    start: null,
    middle: null,
    end: null
  };
  let amount = 0;
  for (const [type, value] of Object.entries(content)) {
    if (value?.trim()) {
      (parsedContent as Record<string, string | null>)[type] = value;
      amount++;
    }
  }
  return { content: parsedContent, amount };
}

function _getTitleBounds(edge: EdgeForBounds): EdgeTitleBounds {
  const titleBounds: EdgeTitleBounds = { y: null, height: null, vAlign: null, shiftReversed: 0 };
  const { width: edgeWidth } = edge.edgeBounds;
  const { startLabelPadding, endLabelPadding } = edge.arrowsBounds;

  const contentParsed: ParsedContent | null = edge.content
    ? _contentParsed(edge.content)
    : {
        amount: 1,
        content: { start: null, middle: edge.contentMiddle, end: null }
      };

  if (!contentParsed?.amount || !edge.fontSize) {
    return edge.titleBounds = { x: 0, y: 0, height: 0, shiftReversed: 0 };
  }

  const fontConfig = {
    fontSize: edge.fontSize,
    fontFamily: edge.fontFamily,
    fontWeight: edge.fontWeight,
    fontStyle: edge.fontStyle,
    lineHeight: edge.fontSize * 1.1,
  };

  const labelWidth = (edgeWidth - startLabelPadding - endLabelPadding) / contentParsed.amount;
  const labelsHeight: number[] = [];

  if (labelWidth < 30) return edge.titleBounds = { x: 0, y: 0, height: 0, shiftReversed: 0 };

  for (const [_label, labelText] of Object.entries(contentParsed.content)) {
    if (labelText) {
      const { textHeight } = measureTextDimensions(edge.ctx, labelText, fontConfig, labelWidth);
      labelsHeight.push(textHeight);
    }
  }

  titleBounds.height = Math.max(...labelsHeight);
  const textPaddingFromEdge = 5 + (edge.borderWidth - 1) / 2;

  if (!edge.textPosition) edge.textPosition = TEXT_POSITION.TOP.VALUE;
  switch (edge.textPosition) {
    case TEXT_POSITION.TOP.VALUE: {
      titleBounds.vAlign = TEXT_POSITION.BOTTOM.VALUE;
      titleBounds.y = -(titleBounds.height! + textPaddingFromEdge);
      titleBounds.shiftReversed = textPaddingFromEdge;
      break;
    }
    case TEXT_POSITION.CENTER.VALUE: {
      titleBounds.vAlign = TEXT_POSITION.CENTER.VALUE;
      titleBounds.y = titleBounds.shiftReversed = -(titleBounds.height! / 2);
      break;
    }
    case TEXT_POSITION.BOTTOM.VALUE: {
      titleBounds.vAlign = TEXT_POSITION.TOP.VALUE;
      titleBounds.y = textPaddingFromEdge;
      titleBounds.shiftReversed = -(titleBounds.height! + textPaddingFromEdge);
      break;
    }
  }

  titleBounds.padding = 5 + (edge.borderWidth - 1) / 2;
  return edge.titleBounds = titleBounds;
}

export function calculateBoundsForEdge(edge: EdgeForBounds): BoundsResult {
  const bounds: BoundsResult = { width: null, height: null };

  const { width: edgeWidth, height: edgeHeight } = edge.name ? _getEdgeTemplateBounds(edge) : _getEdgeBounds(edge);

  // World-space edges only need connection points (set by _getEdgeBounds).
  // Skip the rest — no cache canvas, and ctx is null so text measurement would crash.
  const edgeType = getEdgeType(edge.routingType ?? 'straight');
  if (edgeType.worldSpace && !edge.name) {
    edge.arrowsBounds = { startArrowWidth: 0, startArrowHeight: 0, startLabelPadding: 0, endArrowWidth: 0, endArrowHeight: 0, endLabelPadding: 0 };
    edge.shadowBounds = { width: edgeWidth, height: edgeHeight, x: 0, y: 0, blurWidth: 0, shadowShiftX: 0, shadowShiftY: 0 };
    edge.titleBounds = { x: 0, y: 0, height: 0, shiftReversed: 0 };
    return bounds;
  }

  const { startArrowHeight, endArrowHeight } = _getArrowsBounds(edge);
  const { x: shadowX, y: shadowY, blurWidth: shadowBlurWidth } = _getShadowBounds(edge);
  const { y: titleY, height: titleHeight, padding: titlePadding } = _getTitleBounds(edge);

  // Shadow computed
  const shadowWidthLeft = shadowX! < 0 ? shadowX! : 0;
  const shadowWidthRight = shadowX! < 0 ? shadowBlurWidth! * 2 - Math.abs(shadowX!) : shadowBlurWidth! * 2 + shadowX!;
  const shadowHeightTop = shadowY! < 0 ? shadowY! : 0;
  const shadowHeightBottom = shadowY! < 0 ? shadowBlurWidth! * 2 - Math.abs(shadowY!) : shadowBlurWidth! * 2 + shadowY!;

  // Title computed
  const titleHeightTop = titleY! < 0 ? titleY! : 0;
  const titleHeightBottom =
    titleY! > 0
      ? titleHeight! + (titlePadding ?? 0)
      : Math.abs(titleY!) === titleHeight! / 2
        ? titleHeight! / 2
        : 0;

  // Arrows computed
  const arrowHeightTopBottom = Math.max(startArrowHeight, endArrowHeight) / 2;

  // Width and height computed
  const leftPart = Math.abs(Math.min(0, shadowWidthLeft));
  const rightPart = Math.abs(Math.max(0, shadowWidthRight));
  const topPart = Math.abs(Math.min(0, shadowHeightTop, titleHeightTop, -arrowHeightTopBottom));
  const bottomPart = Math.abs(Math.max(0, shadowHeightBottom, titleHeightBottom, arrowHeightTopBottom));

  bounds.width = edgeWidth + leftPart + rightPart;
  bounds.height = edgeHeight + topPart + bottomPart;

  return bounds;
}

export type { EdgeForBounds, BoundsResult };
